import { FileImportType, ReplenishType } from 'commons/enums';
import {
  ICompanyInfo,
  IInboundInvoiceIdentifier,
  ProductDetail,
} from 'commons/types';
import { IFileImport } from 'commons/types/fileImport.interface';

import { SwipeRxWmsApi } from '../resources';
import { SwipeRxAPIResources } from '../resources/types';
import { InboundTaskItemAssignUnassignRequest } from '../SwipeRxWmsApiInboundTaskItem/type';

import {
  BinCommodityListResponse,
  BinListResponse,
  BinPaginatedResponse,
  BulkPrioritizeResProps,
  DeliveryNoteListResponse,
  IBinProductDetailRequest,
  IBulkCancelDeliveryNoteResult,
  IBulkCancelStockTransferResult,
  IBinProductDetailResponse,
  IBulkUpdateRetryInboundInvoiceResProps,
  InboundInvoiceListResponse,
  InboundTaskDocumentListResponse,
  InboundTaskItemListResponse,
  IPatchUpdateBinCommodityNameRequest,
  IPatchUpdateBinCommodityStatusRequest,
  IPostCreateBinCommodityRequest,
  IPostCreateBinRequest,
  IPostUpdateBinCommodityRequest,
  IPostUpdateBinInventoryNumberRequest,
  IPostUpdateBinQuantityRequest,
  IPostUpdateBinTypeRequest,
  IRemoveReplenishQueueResProps,
  IRetryCreateBinRequest,
  OrderListResponse,
  ReplenishListResponse,
  StockTransferListResponse,
  STODocumentListResponse,
  STOItemListResponse,
  UserAPIResponse,
  UserListResponse,
} from './types';
import { InventoryAdjustmentListResponse } from './types/get-inventory-adjustment-list.type';
import {
  PackingActiveOrderSummariesResponse,
  PackingShiftSummariesResponse,
  PickingActiveOrderSummariesResponse,
  PickingShiftSummariesResponse,
} from './types/get-summaries.type';
import { IBulkUpdateRetryResProps } from './types/update-retry.interface';

class SwipeRxWmsApiDashboard extends SwipeRxWmsApi {
  private readonly basePath: string;

  constructor() {
    super(SwipeRxAPIResources.DASHBOARD);
    this.basePath = `/${this.apiResource}`;
  }

  async getOrderListPaginated(
    queries: Record<string, string | number>,
  ): Promise<OrderListResponse> {
    const result = await this.get(`${this.basePath}/order-list`, queries);
    if (result && result.data) {
      return result.data as OrderListResponse;
    }
    return [];
  }

  async getDeliveryNoteListPaginated(
    queries: Record<string, string | number>,
  ): Promise<DeliveryNoteListResponse> {
    const result = await this.get(`${this.basePath}/delivery-note`, queries);
    if (result && result.data) {
      return result.data as DeliveryNoteListResponse;
    }
    return [];
  }

  async cancelDeliveryNote(awbNumber: string): Promise<void> {
    try {
      await this.patch(`${this.basePath}/delivery-note/cancel`, {
        awb_number: awbNumber,
      });
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async cancelDeliveryNotesBulk(
    awbNumbers: string[],
  ): Promise<IBulkCancelDeliveryNoteResult> {
    try {
      const result = await this.patch(
        `${this.basePath}/delivery-note/cancel/bulk`,
        { awb_numbers: awbNumbers },
      );
      return result.data as IBulkCancelDeliveryNoteResult;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async getPickingSummariesActiveOrders(
    location: number | undefined,
  ): Promise<PickingActiveOrderSummariesResponse | null> {
    const result = await this.get(
      `${this.basePath}/picking-summaries/active-orders`,
      {
        location,
      },
    );
    if (result && result.data) {
      return result.data as PickingActiveOrderSummariesResponse;
    }
    return null;
  }

  async getPickingSummariesShift(
    location: number | undefined,
  ): Promise<PickingShiftSummariesResponse | null> {
    const result = await this.get(`${this.basePath}/picking-summaries/shift`, {
      location,
    });
    if (result && result.data) {
      return result.data as PickingShiftSummariesResponse;
    }
    return null;
  }

  async getPackingSummariesActiveOrders(
    location: number | undefined,
  ): Promise<PackingActiveOrderSummariesResponse | null> {
    const result = await this.get(
      `${this.basePath}/packing-summaries/active-orders`,
      {
        location,
      },
    );
    if (result && result.data) {
      return result.data as PackingActiveOrderSummariesResponse;
    }
    return null;
  }

  async getPackingSummariesShift(
    location: number | undefined,
  ): Promise<PackingShiftSummariesResponse | null> {
    const result = await this.get(`${this.basePath}/packing-summaries/shift`, {
      location,
    });
    if (result && result.data) {
      return result.data as PackingShiftSummariesResponse;
    }
    return null;
  }

  async getFailedOrders(location: number | undefined): Promise<number> {
    const result = await this.get(`${this.basePath}/failed-orders`, {
      location,
    });
    return result.data as number;
  }

  async prioritizeOrderPick(
    poNumber: string,
    location?: number,
  ): Promise<boolean> {
    try {
      const result = await this.post(`${this.basePath}/prioritize-pick`, {
        location,
        po_number: poNumber,
      });
      return result.data;
    } catch (error: any) {
      console.warn(error);
      throw error;
    }
  }

  async bulkPrioritizeOrderPick(
    poNumbers: string[],
    location?: number,
  ): Promise<BulkPrioritizeResProps> {
    try {
      const result = await this.post(`${this.basePath}/prioritize-pick/bulk`, {
        location,
        po_numbers: poNumbers,
      });
      return result.data;
    } catch (error: any) {
      console.warn(error);
      throw error;
    }
  }

  async unPrioritizeOrderPick(
    poNumber: string,
    location?: number,
  ): Promise<boolean> {
    try {
      const result = await this.post(`${this.basePath}/unprioritize-pick`, {
        location,
        po_number: poNumber,
      });
      return result.data;
    } catch (error: any) {
      console.warn(error);
      throw error;
    }
  }

  async bulkUnPrioritizeOrderPick(
    poNumbers: string[],
    location?: number,
  ): Promise<BulkPrioritizeResProps> {
    try {
      const result = await this.post(
        `${this.basePath}/unprioritize-pick/bulk`,
        {
          location,
          po_numbers: poNumbers,
        },
      );
      return result.data;
    } catch (error: any) {
      console.warn(error);
      throw error;
    }
  }

  async getReplenishListPaginated(
    queries: Record<string, string | number>,
  ): Promise<ReplenishListResponse> {
    const result = await this.get(`${this.basePath}/replenish-list`, queries);
    if (result && result.data) {
      return result.data as ReplenishListResponse;
    }
    return [];
  }

  async getUserListPaginated(
    queries: Record<string, string | number>,
  ): Promise<UserListResponse> {
    try {
      const result = await this.get(`${this.basePath}/users`, queries);
      return result.data.map((user: UserAPIResponse) => ({
        userName: user.name,
        roles: user.roles,
        warehouse: user.location,
        userStatus: user.user_status,
      }));
    } catch (error: any) {
      console.warn(error);
      throw error;
    }
  }

  async getBinListPaginated(
    queries: Record<string, string | number>,
  ): Promise<BinPaginatedResponse> {
    const result = await this.get(`${this.basePath}/bin-list`, queries);
    if (result && result.data) {
      return result.data as BinPaginatedResponse;
    }
    return [];
  }

  async getBinListWithSkuPaginated(
    queries: Record<string, string | number>,
  ): Promise<BinListResponse> {
    const result = await this.get(
      `${this.basePath}/bin-list-with-sku`,
      queries,
    );
    if (result && result.data) {
      return result.data as BinListResponse;
    }
    return [];
  }

  async getBinCommodities(params?: {
    is_enabled?: boolean;
  }): Promise<BinCommodityListResponse> {
    const result = await this.get(`${this.basePath}/bin-commodities`, params);
    if (result && result.data) {
      return result.data as BinCommodityListResponse;
    }
    return [];
  }

  async createBinCommodity(name: string): Promise<void> {
    try {
      const payload: IPostCreateBinCommodityRequest = { name };
      const result = await this.post(
        `${this.basePath}/bin-commodities`,
        payload,
      );
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async updateBinCommodityName(id: string, name: string): Promise<void> {
    try {
      const payload: IPatchUpdateBinCommodityNameRequest = { name };
      const result = await this.patch(
        `${this.basePath}/bin-commodities/${id}`,
        payload,
      );
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async updateBinCommodityStatus(
    id: string,
    isEnabled: boolean,
  ): Promise<void> {
    try {
      const payload: IPatchUpdateBinCommodityStatusRequest = {
        is_enabled: isEnabled,
      };
      const result = await this.patch(
        `${this.basePath}/bin-commodities/${id}`,
        payload,
      );
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async deleteBinCommodity(id: string): Promise<void> {
    try {
      const result = await this.delete(
        `${this.basePath}/bin-commodities/${id}`,
      );
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async getStockTransferListPaginated(
    queries: Record<string, string | number>,
  ): Promise<StockTransferListResponse> {
    const result = await this.get(
      `${this.basePath}/stock-transfer-list`,
      queries,
    );
    if (result && result.data) {
      return result.data as StockTransferListResponse;
    }
    return [];
  }

  async getInventoryAdjustmentListPaginated(
    queries: Record<string, string | number>,
  ): Promise<InventoryAdjustmentListResponse> {
    const result = await this.get(
      `${this.basePath}/inventory-adjustment`,
      queries,
    );
    if (result && result.data) {
      return result.data as InventoryAdjustmentListResponse;
    }
    return [];
  }

  async retryUpdate(poNumber: string, location?: number): Promise<boolean> {
    try {
      const result = await this.post(`${this.basePath}/retry`, {
        location,
        po_number: poNumber,
      });
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async bulkRetryUpdate(
    poNumbers: string[],
    location?: number,
  ): Promise<IBulkUpdateRetryResProps> {
    try {
      const result = await this.post(`${this.basePath}/retry/bulk`, {
        location,
        po_numbers: poNumbers,
      });
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async syncAwb(poNumbers: string[]): Promise<void> {
    try {
      await this.post(`${this.basePath}/sync-awb`, {
        po_numbers: poNumbers,
      });
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async getActiveLocations(): Promise<ICompanyInfo[]> {
    try {
      const result = await this.get(`${this.basePath}/active-locations`);
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async prioritizeReplenishTask(
    replenishNumber: string,
    location?: number,
  ): Promise<boolean> {
    try {
      const result = await this.post(`${this.basePath}/prioritize-replenish`, {
        location,
        replenish_number: replenishNumber,
      });
      return result.data;
    } catch (error: any) {
      console.warn(error);
      throw error;
    }
  }

  async bulkPrioritizeReplenishTask(
    replenishNumbers: string[],
    location?: number,
  ): Promise<BulkPrioritizeResProps> {
    try {
      const result = await this.post(
        `${this.basePath}/prioritize-replenish/bulk`,
        {
          location,
          replenish_numbers: replenishNumbers,
        },
      );
      return result.data;
    } catch (error: any) {
      console.warn(error);
      throw error;
    }
  }

  async unPrioritizeReplenishTask(
    replenishNumber: string,
    location?: number,
  ): Promise<boolean> {
    try {
      const result = await this.post(
        `${this.basePath}/unprioritize-replenish`,
        {
          location,
          replenish_number: replenishNumber,
        },
      );
      return result.data;
    } catch (error: any) {
      console.warn(error);
      throw error;
    }
  }

  async bulkUnPrioritizeReplenishTask(
    replenishNumbers: string[],
    location?: number,
  ): Promise<BulkPrioritizeResProps> {
    try {
      const result = await this.post(
        `${this.basePath}/unprioritize-replenish/bulk`,
        {
          location,
          replenish_numbers: replenishNumbers,
        },
      );
      return result.data;
    } catch (error: any) {
      console.warn(error);
      throw error;
    }
  }

  async setCancelNonPickedTask(
    poNumber: string,
    location?: number,
  ): Promise<boolean> {
    try {
      const result = await this.post(`${this.basePath}/set-non-picked`, {
        location,
        po_number: poNumber,
      });
      return result.data;
    } catch (error: any) {
      console.warn(error);
      throw error;
    }
  }

  async retryUpdateReplenish(
    replenishNumber: string,
    location?: number,
  ): Promise<boolean> {
    try {
      const result = await this.post(`${this.basePath}/retry-replenish`, {
        location,
        replenish_number: replenishNumber,
      });
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async bulkRetryUpdateReplenish(
    replenishNumbers: string[],
    location?: number,
  ): Promise<IBulkUpdateRetryResProps> {
    try {
      const result = await this.post(`${this.basePath}/retry-replenish/bulk`, {
        location,
        replenish_numbers: replenishNumbers,
      });
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async removeQueue(
    replenishType: ReplenishType,
    location?: number,
  ): Promise<IRemoveReplenishQueueResProps> {
    try {
      const result = await this.post(`${this.basePath}/replenish/remove`, {
        location,
        replenish_type: replenishType,
      });
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async retryUpdateStockTransfer(
    transferNumbers: string[],
    location?: number,
  ): Promise<IBulkUpdateRetryResProps> {
    try {
      const result = await this.post(`${this.basePath}/retry-stock-transfer`, {
        location,
        transfer_numbers: transferNumbers,
      });
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async bulkCancelStockTransfer(
    transferNumbers: string[],
    location?: number,
  ): Promise<IBulkCancelStockTransferResult> {
    try {
      const result = await this.patch(
        `${this.basePath}/stock-transfer/cancel/bulk`,
        {
          location,
          transfer_numbers: transferNumbers,
        },
      );
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async bulkUpload(
    file: File,
    fileImportType: FileImportType,
    location?: number,
  ): Promise<IFileImport> {
    try {
      const form = new FormData();
      form.append(
        'file',
        new Blob(
          [await file.arrayBuffer()],

          { type: 'text/csv' },
        ),
        file.name,
      );
      form.append('file_import_type', fileImportType);
      if (location) form.append('location', location.toString());

      let bulkUploadEndpoint = 'bulk-upload';
      if (
        [
          FileImportType.ADD_USER,
          FileImportType.DELETE_USER,
          FileImportType.PHARMACY_COPY_INVOICE_FLAG,
        ].includes(fileImportType)
      ) {
        bulkUploadEndpoint = 'super-admin-bulk-upload';
      }

      const result = await this.post(
        `${this.basePath}/${bulkUploadEndpoint}`,
        form,
        {
          headers: {
            'content-type': 'multipart/form-data',
          },
        },
      );
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async getBulkUploadHistoryPaginated(
    queries: Record<string, string | number>,
  ): Promise<IFileImport[]> {
    try {
      const result = await this.get(`${this.basePath}/upload-history`, queries);
      return result.data;
    } catch (error: any) {
      console.warn(error);
      throw error;
    }
  }

  async completePacking(poNumber: string, location?: number): Promise<void> {
    try {
      const result = await this.post(`${this.basePath}/complete-packing`, {
        warehouse_id: location,
        po_number: poNumber,
      });
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async postCreateBin(
    bin: Pick<ProductDetail, 'bin' | 'bin_type' | 'commodity'>,
    location?: number,
  ): Promise<void> {
    try {
      const payload: IPostCreateBinRequest = {
        ...bin,
        location,
      };
      const result = await this.post(`${this.basePath}/bins`, payload);
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async retryUpdateInventoryAdjustment(
    documentNumber: string,
    location?: number,
  ): Promise<boolean> {
    try {
      const result = await this.post(
        `${this.basePath}/retry-inventory-adjustment`,
        {
          location,
          document_number: documentNumber,
        },
      );
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async updateBinQuantity(bin: IPostUpdateBinQuantityRequest): Promise<void> {
    try {
      const result = await this.patch(`${this.basePath}/bins/quantity`, bin);
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async updateBinInventoryNumber(
    bin: IPostUpdateBinInventoryNumberRequest,
  ): Promise<void> {
    try {
      const result = await this.patch(
        `${this.basePath}/bins/inventory-number`,
        bin,
      );
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async bulkRetryUpdateInventoryAdjustment(
    documentNumbers: string[],
    location?: number,
  ): Promise<IBulkUpdateRetryResProps> {
    try {
      const result = await this.post(
        `${this.basePath}/retry-inventory-adjustment/bulk`,
        {
          location,
          document_numbers: documentNumbers,
        },
      );
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async updateBinType(bin: IPostUpdateBinTypeRequest): Promise<void> {
    try {
      const result = await this.patch(`${this.basePath}/bins/type`, bin);
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async updateBinCommodity(bin: IPostUpdateBinCommodityRequest): Promise<void> {
    try {
      const result = await this.patch(`${this.basePath}/bins/commodity`, bin);
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async retryCreateBin(bin: IRetryCreateBinRequest): Promise<boolean> {
    try {
      const result = await this.post(`${this.basePath}/bins/retry`, bin);
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async getBinProductDetail(params: IBinProductDetailRequest): Promise<{
    error?: string | true;
    data?: IBinProductDetailResponse;
  }> {
    try {
      const result = await this.get(`${this.basePath}/product-detail`, params);
      const data = result.data as IBinProductDetailResponse;
      return { data };
    } catch (error: unknown) {
      if (error instanceof Error) {
        return { error: error.message };
      }
      return { error: true };
    }
  }

  async getInboundTaskDocumentListPaginated(
    queries: Record<string, string | number>,
  ): Promise<InboundTaskDocumentListResponse> {
    const result = await this.get(
      `${this.basePath}/inbound/documents`,
      queries,
    );
    if (result && result.data) {
      return result.data as InboundTaskDocumentListResponse;
    }
    return [];
  }

  async getInboundTaskItemList(
    inboundTaskNumber: string,
  ): Promise<InboundTaskItemListResponse> {
    const result = await this.get(
      `${this.basePath}/inbound/documents/${encodeURIComponent(
        inboundTaskNumber,
      )}/list`,
    );
    if (result && result.data) {
      return result.data as InboundTaskItemListResponse;
    }
    return [];
  }

  async getInboundInvoiceListPaginated(
    queries: Record<string, string | number>,
  ): Promise<InboundInvoiceListResponse> {
    const result = await this.get(`${this.basePath}/inbound/invoices`, queries);
    if (result && result.data) {
      return result.data as InboundInvoiceListResponse;
    }
    return [];
  }

  async getInboundInvoiceItemList(
    inboundTaskNumber: string,
    invoiceNumber: string,
  ): Promise<InboundTaskItemListResponse> {
    const result = await this.get(
      `${this.basePath}/inbound/invoices/${encodeURIComponent(
        inboundTaskNumber,
      )}/${encodeURIComponent(invoiceNumber)}/list`,
    );
    if (result && result.data) {
      return result.data as InboundTaskItemListResponse;
    }
    return [];
  }

  async retryUpdateInboundInvoice(
    invoiceNumber: string,
    inboundTaskNumber: string,
    location?: number,
  ): Promise<boolean> {
    try {
      const result = await this.post(`${this.basePath}/retry-inbound-invoice`, {
        location,
        invoice_number: invoiceNumber,
        inbound_task_number: inboundTaskNumber,
      });
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async bulkRetryUpdateInboundInvoice(
    invoiceIdentifiers: IInboundInvoiceIdentifier[],
    location?: number,
  ): Promise<IBulkUpdateRetryInboundInvoiceResProps> {
    try {
      const result = await this.post(
        `${this.basePath}/retry-inbound-invoice/bulk`,
        {
          location,
          invoice_identifiers: invoiceIdentifiers,
        },
      );
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async resetInboundTaskItemAssignment(
    payload: InboundTaskItemAssignUnassignRequest,
  ): Promise<void> {
    try {
      await this.post(`${this.basePath}/unassign-inbound-item`, payload);
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async getSTODocumentListPaginated(
    queries: Record<string, string | number>,
  ): Promise<STODocumentListResponse> {
    const result = await this.get(`${this.basePath}/sto/list`, queries);
    if (result && result.data) {
      return result.data as STODocumentListResponse;
    }
    return [];
  }

  async getSTOItemList(stoNumber: string): Promise<STOItemListResponse> {
    const result = await this.get(`${this.basePath}/sto/${stoNumber}/list`);
    if (result && result.data) {
      return result.data as STOItemListResponse;
    }
    return [];
  }

  async setSTODocumentArrived(
    stoNumber: string,
    location?: number,
  ): Promise<boolean> {
    try {
      const result = await this.post(`${this.basePath}/sto/arrived`, {
        location,
        sto_number: stoNumber,
      });
      return result.data;
    } catch (error: any) {
      console.warn(error);
      throw error;
    }
  }

  async bulkSetSTODocumentArrived(
    stoNumbers: string[],
    location?: number,
  ): Promise<BulkPrioritizeResProps> {
    try {
      const result = await this.post(`${this.basePath}/sto/arrived/bulk`, {
        location,
        sto_numbers: stoNumbers,
      });
      return result.data;
    } catch (error: any) {
      console.warn(error);
      throw error;
    }
  }

  async retryUpdateFailedSTO(
    stoNumbers: string[],
    location?: number,
  ): Promise<IBulkUpdateRetryResProps> {
    try {
      const result = await this.post(`${this.basePath}/sto/ship/retry`, {
        location,
        sto_numbers: stoNumbers,
      });
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }
}

export const swiperxWmsApiDashboard = new SwipeRxWmsApiDashboard();
